'use client';

import { createTheme } from '@mui/material/styles';

/* The Adzilio design system, expressed as a MUI theme. Values are the exact
   production tokens — not approximations. Orbitron is confined to `overline`
   (the small uppercase AI accent labels) exactly as the product rules require;
   everything else is Inter. */

export const tokens = {
  ground: '#F4F5FA',
  surface: '#FFFFFF',
  soft: '#F8F8FD',
  ink: '#111630',
  ink2: '#333A58',
  muted: '#707894',
  line: '#E4E6EF',
  purple: '#5B37EE',
  purple2: '#8449F4',
  accent: '#A878FF',
  blue: '#2563EB',
  cyan: '#0E9ADF',
  green: '#11AE62',
  orange: '#F07A22',
  red: '#EF4444',
  navy: '#0C0E19',
  /* the raised surface on navy, same value the film uses */
  navySurface: '#151827',
  /* the light-on-dark counterparts of ink / ink2 / muted / line */
  inkOn: '#EDEAFF',
  ink2On: 'rgba(226,225,255,.76)',
  mutedOn: 'rgba(226,225,255,.58)',
  lineOn: 'rgba(168,120,255,.18)',
  grad: 'linear-gradient(135deg,#5B37EE,#8449F4)',
  darkBg: 'radial-gradient(120% 120% at 18% 0%,#1A2044 0%,#0F1328 45%,#0C0E19 100%)',
  ease: 'cubic-bezier(.22,1,.36,1)',
  easeSettle: 'cubic-bezier(.62,.02,.24,1)',
  shadow: '0 16px 40px rgba(0,0,0,.34)',
  shadowUp: '0 26px 64px rgba(0,0,0,.46)',
};

const sans = 'var(--font-sans), system-ui, -apple-system, "Segoe UI", sans-serif';
const brand = 'var(--font-brand), var(--font-sans), sans-serif';

const theme = createTheme({
  spacing: 8,
  shape: { borderRadius: 16 },
  palette: {
    mode: 'dark',
    primary: { main: tokens.purple, light: tokens.purple2, dark: '#4526C9', contrastText: '#fff' },
    secondary: { main: tokens.accent, light: tokens.accent, contrastText: '#0C0E19' },
    success: { main: tokens.green },
    warning: { main: tokens.orange },
    error: { main: tokens.red },
    info: { main: tokens.blue },
    background: { default: tokens.navy, paper: tokens.navySurface },
    text: { primary: tokens.inkOn, secondary: tokens.ink2On, disabled: tokens.mutedOn },
    divider: tokens.lineOn,
  },
  typography: {
    fontFamily: sans,
    htmlFontSize: 16,
    h1: { fontFamily: sans, fontWeight: 800, letterSpacing: '-0.035em', lineHeight: 1.03,
          fontSize: 'clamp(2.4rem,6vw,4.6rem)' },
    h2: { fontFamily: sans, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.1,
          fontSize: 'clamp(1.75rem,3.6vw,2.7rem)' },
    h3: { fontFamily: sans, fontWeight: 700, letterSpacing: '-0.01em', fontSize: '1.06rem' },
    body1: { fontSize: '1.02rem', lineHeight: 1.6, color: tokens.ink2On },
    body2: { fontSize: '0.94rem', lineHeight: 1.6, color: tokens.mutedOn },
    button: { fontWeight: 600, textTransform: 'none', letterSpacing: 0 },
    /* the ONLY place Orbitron is allowed */
    overline: {
      fontFamily: brand, fontSize: 11, fontWeight: 600, letterSpacing: '0.2em',
      textTransform: 'uppercase', lineHeight: 1.6, display: 'block',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        'html, body': { margin: 0, padding: 0 },
        body: {
          backgroundColor: tokens.navy,
          backgroundImage: tokens.darkBg,
          /* fixed so the bloom stays anchored to the viewport instead of being
             stretched down the length of the page */
          backgroundAttachment: 'fixed',
          WebkitFontSmoothing: 'antialiased',
        },
        "body[data-locked='true']": { overflow: 'hidden' },
        '::selection': { background: 'rgba(168,120,255,.28)' },
        /* the film engine's authoring playback bar has no place on a site */
        '[data-omelette-chrome]': { display: 'none !important' },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 11, height: 48, paddingInline: 22, fontSize: '0.97rem' },
        outlined: {
          background: 'rgba(255,255,255,.04)', borderColor: tokens.lineOn, color: tokens.inkOn,
          '&:hover': { background: 'rgba(255,255,255,.08)', borderColor: 'rgba(168,120,255,.42)' },
        },
        sizeSmall: { height: 42, paddingInline: 16, fontSize: '0.9rem' },
      },
      variants: [
        {
          props: { variant: 'contained', color: 'primary' },
          style: {
            background: tokens.grad,
            boxShadow: '0 14px 30px -12px rgba(91,55,238,.6)',
            '&:hover': {
              background: tokens.grad,
              boxShadow: '0 18px 38px -12px rgba(91,55,238,.72)',
            },
          },
        },
      ],
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { backgroundImage: 'none' },
        outlined: { borderColor: tokens.lineOn, boxShadow: tokens.shadow },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 999, fontWeight: 500 },
        outlined: { borderColor: tokens.lineOn, background: 'rgba(255,255,255,.04)', color: tokens.ink2On },
      },
    },
    MuiContainer: {
      defaultProps: { maxWidth: 'xl' },
      styleOverrides: {
        root: {
          '@media (min-width:600px)': { paddingLeft: 32, paddingRight: 32 },
          '@media (min-width:1200px)': { paddingLeft: 56, paddingRight: 56 },
        },
      },
    },
    MuiLink: { defaultProps: { underline: 'hover' } },
  },
});

export default theme;
